import {type VariantProps} from 'class-variance-authority';
import {
  CircleAlert,
  CircleCheck,
  CircleX,
  Info,
  TriangleAlert,
  X,
} from 'lucide-react';
import {Toast as ToastPrimitives} from 'radix-ui';
import * as React from 'react';

import {cn} from '@/utils/ui';

import {buttonVariants} from '../button';
import {Typography} from '../typography';
import {toastVariants} from './toast-variants';

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({className, ...props}, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed top-4 left-1/2 z-100 flex w-[calc(100%-2rem)] max-w-[360px] -translate-x-1/2',
      className
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>['variant']>;

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({className, variant, ...props}, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({variant}), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const toastIcons = {
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleX,
  general: Info,
  default: Info,
} as const;

function ToastIcon({variant}: {variant?: ToastVariant | null}) {
  const normalizedVariant = variant === 'destructive' ? 'error' : variant;
  const Icon =
    (normalizedVariant && toastIcons[normalizedVariant]) ?? CircleAlert;

  return (
    <span
      data-slot="toast-icon"
      className="flex size-6 shrink-0 items-center justify-center rounded-full p-1"
    >
      <Icon className="size-4" />
    </span>
  );
}

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({className, ...props}, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      buttonVariants({variant: 'link', size: 'small'}),
      'h-9 w-fit px-4 py-2 text-current hover:bg-black/5 group-[.toast-general]:hover:bg-white/10 focus-visible:ring-current disabled:pointer-events-none disabled:opacity-50',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({className, ...props}, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      'flex size-11 shrink-0 items-center justify-center rounded-full text-current transition-colors',
      'hover:bg-black/5 group-[.toast-general]:hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-current focus-visible:outline-none [&>svg]:size-5',
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="size-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({className, ...props}, ref) => (
  <Typography asChild variant="body2" className={cn('text-current', className)}>
    <ToastPrimitives.Title ref={ref} {...props} />
  </Typography>
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({className, ...props}, ref) => (
  <Typography
    asChild
    variant="body2Regular"
    className={cn('text-current/80', className)}
  >
    <ToastPrimitives.Description ref={ref} {...props} />
  </Typography>
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  Toast,
  ToastAction,
  type ToastActionElement,
  ToastClose,
  ToastDescription,
  ToastIcon,
  type ToastProps,
  ToastProvider,
  ToastTitle,
  ToastViewport,
};
